import { useEffect, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import MarkerClusterGroup from "react-leaflet-cluster";
import { useNavigate } from "react-router-dom";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useTranslation } from "react-i18next";

// ✅ Default Marker fix
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const API_BASE = process.env.REACT_APP_API_BASE_URL;

function FitBounds({ coords }) {
  const map = useMap();
  useEffect(() => {
    if (coords.length > 0) {
      map.fitBounds(coords, { padding: [50, 50] });
    }
  }, [coords, map]);
  return null;
}

// ✅ mikro kaydırma (2x artırıldı)
function jitter(val) {
  return val + (Math.random() - 0.5) * 0.0002;
}

// ✅ Tip → Harf + Renk eşlemesi
function getDeviceStyle(type = "") {
  const t = type?.toUpperCase?.() || "";
  let letter = "D";
  let color = "#43a047";

  // 🔵 Analyzer (PAD)
  if (t.startsWith("PAD")) {
    letter = "A";
    color = "#1E88E5";
  }

  // 🟣 Multimetre (PMA, PMD)
  else if (t.startsWith("PMA") || t.startsWith("PMD")) {
    letter = "M";
    color = "#8E24AA"; // mor
  }

  // 🔴 Reactive (PRC)
  else if (t.startsWith("PRC")) {
    letter = "R";
    color = "#E53935";
  }

  // 🟢 Gateway
  else if (t.startsWith("GATEWAY")) {
    letter = "G";
    color = "#2E7D32";
  }

  return { letter, color };
}


export default function DeviceMapPage() {
  const { t } = useTranslation();
  const [gateways, setGateways] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    async function fetchCoords() {
      try {
        const res = await fetch(`${API_BASE}/gateways/with-coords`, {
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) throw new Error("HTTP " + res.status);
        const json = await res.json();
        setGateways(json);
      } catch (err) {
        console.error("❌ coords fetch error:", err);
      }
    }
    fetchCoords();
  }, []);

  const coords = [];
  gateways.forEach((gw) => {
    if (gw.latitude != null && gw.longitude != null)
      coords.push([gw.latitude, gw.longitude]);
    gw.devices.forEach((dev) => {
      if (dev.latitude != null && dev.longitude != null)
        coords.push([dev.latitude, dev.longitude]);
    });
  });

  // ✅ Cihaz dashboard rotasına yönlendirme
  function goToDashboard(dev) {
    const type = dev.type?.toUpperCase?.() || "";
    let deviceType = "analyzer";
    if (type.startsWith("PRC")) deviceType = "reactive";

    localStorage.setItem("deviceType", deviceType);
    navigate(`/devices/${dev.id}/${deviceType}/real-time`);
  }

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <div className="rounded-lg overflow-hidden shadow-md border border-gray-200">
        <MapContainer
          zoom={6}
          maxZoom={18}
          style={{ height: "600px", width: "100%" }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution='&copy; OpenStreetMap contributors'
          />


          <FitBounds coords={coords} />

          <MarkerClusterGroup
            chunkedLoading
            spiderfyOnClick={false}
            showCoverageOnHover={false}
            zoomToBoundsOnClick={true}
            disableClusteringAtZoom={15}
            iconCreateFunction={(cluster) => {
              const count = cluster.getChildCount();
              let bg = "green";
              if (count > 50) bg = "red";
              else if (count > 20) bg = "orange";

              return L.divIcon({
                html: `
                  <div style="
                    background:${bg};
                    color:white;
                    border-radius:50%;
                    width:40px;
                    height:40px;
                    display:flex;
                    align-items:center;
                    justify-content:center;
                    font-weight:bold;
                    border:2px solid white;
                    box-shadow:0 0 6px rgba(0,0,0,0.3);
                  ">${count}</div>`,
                className: "",
                iconSize: [40, 40],
              });
            }}
          >
            {/* ✅ Gateway markerları */}
            {gateways
              .filter((gw) => gw.latitude != null && gw.longitude != null)
              .map((gw) => (
                <Marker
                  key={`gw-${gw.id}`}
                  position={[jitter(gw.latitude), jitter(gw.longitude)]}
                  icon={L.divIcon({
                    html: `
                      <div style="
                        background:#2E7D32;
                        color:white;
                        border-radius:50%;
                        width:30px;
                        height:30px;
                        display:flex;
                        align-items:center;
                        justify-content:center;
                        font-weight:bold;
                        border:2px solid white;
                        box-shadow:0 0 6px rgba(0,0,0,0.3);
                      ">G</div>`,
                    className: "",
                    iconSize: [30, 30],
                  })}
                >
                  <Popup>
                    <div
                      style={{
                        fontFamily: "Inter, sans-serif",
                        minWidth: "200px",
                        padding: "8px",
                        borderRadius: "10px",
                        textAlign: "left",
                      }}
                    >
                      <div
                        style={{
                          fontWeight: 600,
                          fontSize: "15px",
                          marginBottom: "6px",
                        }}
                      >
                        🌐 Gateway: {gw.name}
                      </div>
                      <div style={{ fontSize: "13px", color: "#444" }}>
                        ⚙️ {t('common.type')}: <b>{gw.type}</b>
                      </div>
                      <div
                        style={{
                          fontSize: "13px",
                          color: "#444",
                          marginBottom: "8px",
                        }}
                      >
                        📍 {t('common.location')}: {gw.latitude.toFixed(5)},{" "}
                        {gw.longitude.toFixed(5)}
                      </div>
                      <div style={{ fontSize: "13px", color: "#444" }}>
                        🔗 {t('common.deviceCount')}: {gw.devices?.length || 0}
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}

            {/* ✅ Device markerları */}
            {gateways.flatMap((gw) =>
              gw.devices
                .filter(
                  (dev) => dev.latitude != null && dev.longitude != null
                )
                .map((dev) => {
                  const { letter, color } = getDeviceStyle(dev.type);
                  return (
                    <Marker
                      key={`dev-${dev.id}`}
                      position={[jitter(dev.latitude), jitter(dev.longitude)]}
                      icon={L.divIcon({
                        html: `
                          <div style="
                            background:${color};
                            color:white;
                            border-radius:50%;
                            width:26px;
                            height:26px;
                            display:flex;
                            align-items:center;
                            justify-content:center;
                            font-size:12px;
                            font-weight:bold;
                            border:2px solid white;
                            box-shadow:0 0 6px rgba(0,0,0,0.3);
                          ">${letter}</div>`,
                        className: "",
                        iconSize: [26, 26],
                      })}
                    >
                      <Popup>
                        <div
                          style={{
                            fontFamily: "Inter, sans-serif",
                            minWidth: "200px",
                            padding: "8px",
                            borderRadius: "10px",
                            textAlign: "left",
                          }}
                        >
                          <div
                            style={{
                              fontWeight: 600,
                              fontSize: "15px",
                              marginBottom: "6px",
                            }}
                          >
                            🔧 {t('common.device')}: {dev.name}
                          </div>
                          <div style={{ fontSize: "13px", color: "#444" }}>
                            ⚙️ {t('common.type')}: <b>{dev.type}</b>
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#444",
                              marginBottom: "8px",
                            }}
                          >
                            🧩 {t('common.model')}: <b>{dev.model || "-"}</b>
                          </div>
                          <div
                            style={{
                              fontSize: "13px",
                              color: "#444",
                              marginBottom: "8px",
                            }}
                          >
                            📍 {t('common.location')}:{" "}
                            {dev.latitude && dev.longitude
                              ? `${dev.latitude.toFixed(5)}, ${dev.longitude.toFixed(5)}`
                              : t('common.undefined')}
                          </div>
                          <button
                            onClick={() => goToDashboard(dev)}
                            style={{
                              background: "#1E88E5",
                              color: "white",
                              padding: "6px 10px",
                              border: "none",
                              borderRadius: "6px",
                              cursor: "pointer",
                              width: "100%",
                              fontSize: "13px",
                              fontWeight: 500,
                            }}
                          >
                            {t('map.goToDashboard')}
                          </button>
                        </div>
                      </Popup>
                    </Marker>
                  );
                })
            )}
          </MarkerClusterGroup>
        </MapContainer>
      </div>
    </div>
  );
}
